/**
 * Descuentos en palabras y números: las opciones de la pantalla, cómo se leen los montos que escribe la persona y la
 * validación que se hace antes de mandar (la misma que el backend, que es el que manda). Puro y testeado
 * (`descuentos.test.ts`).
 */

import { num } from "../../campana-nueva/utils/format";
import { fecha } from "../../segmentaciones/logic/segmentar";
import type { DescuentoVigente, Descuentos, ReglasParaGuardar } from "../types";

export const R_DESCUENTOS = "/activa/descuentos";

export const MAXIMOS = [10, 15, 20, 25, 30];
export const CONTROLES = [10, 15, 20, 30];
export const DIAS_MEDICION = [7, 14, 30];
export const MAX_VIGENTES = 10;

/** Qué cambia con cada % de control (DISENO, «Descuentos»). El sugerido es el 15%, el de siempre (Alan, 14/10). */
export const AYUDA_CONTROL: Record<number, string> = {
  10: "Llega a más gente, pero cuesta más saber si funcionó.",
  15: "Sugerido: equilibrio entre llegar a muchos y medir bien.",
  20: "Mide mejor, y llega a un poco menos de gente.",
  30: "Mide mejor, pero llega a menos gente.",
};

/**
 * Un monto en pesos como lo escribe la persona: «25000», «25.000» o «$ 25.000». Sin centavos. Vacío → null; lo que no
 * se entiende → NaN.
 */
export function leerPesos(s: string): number | null {
  const t = String(s ?? "").replace(/\$/g, "").replace(/\s+/g, "");
  if (!t) return null;
  if (/^\d+$/.test(t) || /^\d{1,3}(\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ""));
  return NaN;
}

/** Un porcentaje entero («35» o «35%»). Vacío → null; lo que no se entiende → NaN. */
export function leerPorcentaje(s: string): number | null {
  const t = String(s ?? "").replace(/%/g, "").trim();
  if (!t) return null;
  return /^\d+$/.test(t) ? Number(t) : NaN;
}

/** «25000» → «25.000», para mostrar lo guardado en el campo. */
export const pesosParaEditar = (n: number | null) => (n == null ? "" : num(n));

export interface Formulario {
  maximo: number | null;
  margen: string;
  compraMinima: string;
  vigentes: DescuentoVigente[];
  pctControl: number;
  diasMedicion: number;
}

export function formularioDe(d: Descuentos): Formulario {
  return {
    maximo: d.maximo,
    margen: d.margen == null ? "" : String(d.margen),
    compraMinima: pesosParaEditar(d.compraMinima),
    vigentes: d.vigentes.map(({ nombre, condicion, hasta }) => ({ nombre, condicion, hasta })),
    pctControl: d.pctControl,
    diasMedicion: d.diasMedicion,
  };
}

export type Campo = "maximo" | "margen" | "compraMinima";

/** Lo que se puede revisar acá antes de mandar. El backend vuelve a validar todo. */
export function validarFormulario(f: Formulario): { ok: true; reglas: ReglasParaGuardar } | { ok: false; errores: Partial<Record<Campo, string>> } {
  const errores: Partial<Record<Campo, string>> = {};
  if (f.maximo == null || !MAXIMOS.includes(f.maximo)) errores.maximo = "Elegí el descuento máximo.";
  const margen = leerPorcentaje(f.margen);
  if (margen !== null && (!Number.isInteger(margen) || margen < 1 || margen > 100)) errores.margen = "El margen es un número entero de 1 a 100.";
  else if (margen !== null && f.maximo != null && f.maximo > margen) errores.margen = `Tu descuento máximo (${f.maximo}%) es más que este margen. Bajá el máximo o revisá el margen.`;
  const compraMinima = leerPesos(f.compraMinima);
  if (compraMinima !== null && (!Number.isInteger(compraMinima) || compraMinima < 1)) errores.compraMinima = "Escribí un monto en pesos, sin centavos (por ejemplo 25.000), o dejalo vacío.";
  if (Object.keys(errores).length) return { ok: false, errores };
  return {
    ok: true,
    reglas: { maximo: f.maximo as number, margen, compraMinima, vigentes: f.vigentes, pctControl: f.pctControl, diasMedicion: f.diasMedicion },
  };
}

/** Hoy, «AAAA-MM-DD», en la zona del navegador. */
export function hoyLocal(ahora: Date = new Date()): string {
  const d = (n: number) => String(n).padStart(2, "0");
  return `${ahora.getFullYear()}-${d(ahora.getMonth() + 1)}-${d(ahora.getDate())}`;
}

/** «14/10/2026» de un instante ISO, en la zona del navegador. */
export function diaDe(iso: string): string {
  const x = new Date(iso);
  return Number.isNaN(x.getTime()) ? "—" : fecha(hoyLocal(x));
}

/** Un descuento nuevo de «Descuentos que ya tenés»: el problema, o null si se puede agregar. */
export function problemaDelVigente(v: DescuentoVigente, ya: DescuentoVigente[], hoy: string = hoyLocal()): string | null {
  const nombre = v.nombre.trim().replace(/\s+/g, " ");
  if (nombre.length < 2 || nombre.length > 60) return "Escribí el nombre del descuento (de 2 a 60 letras).";
  if (v.condicion.trim().length > 80) return "La condición es muy larga: hasta 80 letras.";
  if (ya.some((x) => x.nombre.trim().toLowerCase() === nombre.toLowerCase())) return `«${nombre}» ya está en la lista.`;
  if (ya.length >= MAX_VIGENTES) return `Podés cargar hasta ${MAX_VIGENTES} descuentos.`;
  if (v.hasta !== null && !/^\d{4}-\d{2}-\d{2}$/.test(v.hasta)) return "Elegí hasta qué día vale.";
  if (v.hasta !== null && v.hasta < hoy) return "Esa fecha ya pasó.";
  return null;
}

/** «Siempre activo», «Hasta el 31/12/2026» o «Venció el 01/10/2026». */
export function textoVigencia(v: DescuentoVigente & { vencido?: boolean }): string {
  if (v.hasta === null) return "Siempre activo";
  return v.vencido ? `Venció el ${fecha(v.hasta)}` : `Hasta el ${fecha(v.hasta)}`;
}
